import { ControlledInput } from "components/Card/ControlledInput";
import { TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import Select from "components/inputs/Select/Select";
import type { OptionType } from "components/inputs/Select/Select";
import {
  LIBERTY_BENEFICIARY_CLASS_OPTIONS,
  LIBERTY_GRS_OR_LEGACY_RCG_OPTIONS,
  LIBERTY_RCG_CARRIER_OPTIONS,
  LIBERTY_REFERRAL_TYPE_OPTIONS,
  LIBERTY_SEGMENTATION_TYPE_OPTIONS,
} from "constants/LibertyMutualConstants";
import { useEffect } from "react";
import type { Control, FieldErrors } from "react-hook-form";
import { useController, useWatch } from "react-hook-form";

type LibertyMutualReferralFieldsProps = {
  // Callers pass in whatever FieldValues shape their own useForm/useFormContext
  // uses (FormData, a plain untyped useForm(), or a dedicated
  // LibertyMutualFormValues) -- react-hook-form's Control<T> isn't
  // structurally compatible across different T, so this is intentionally
  // untyped here rather than forcing all three call sites onto one shape.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  errors: FieldErrors;
  onFieldSave: (field: string, value: string) => Promise<void>;
  readOnly?: boolean;
  // Intake-only: the backend requires these fields for a Liberty submitter
  // (ClientIntakeFileSerializer), so the intake wizard passes required so
  // "Next"/"Submit" catch a blank value before it ever reaches the backend.
  // RCG Carrier's own visibility already matches its required-ness -- it's
  // only mounted (and only required) when GRS/Legacy RCG is "RCG". Leave
  // unset (the default) for the Operations FMOperations tab and the Medical
  // Review AllocationReview tab, where every field stays optional, matching
  // FileSerializer on the backend.
  required?: boolean;
  // Class of Beneficiary (I-IV) is manual-entry only, determined by the LM
  // Medicare Unit after referral -- it's NOT part of intake
  // (ClientIntakeFileSerializer omits it), so the intake wizard leaves this
  // unset (default false) and it only renders in the Operations/Medical Review
  // call sites. Always optional wherever it shows.
  showBeneficiaryClass?: boolean;
};

// The 5 Liberty Mutual referral fields (2 reused, 3 new), shared verbatim
// across the intake wizard's DocumentUploadAndAdjusterIntake (first page),
// the Operations FMOperations tab, and the Medical Review AllocationReview
// tab -- same fields, same conditional (RCG Carrier only shows when
// GRS/Legacy RCG = "RCG"), just wired to whatever save mechanism the
// caller uses.
const LibertyMutualReferralFields = ({
  control,
  errors,
  onFieldSave,
  readOnly,
  required,
  showBeneficiaryClass,
}: LibertyMutualReferralFieldsProps) => {
  const requiredRule = required
    ? { required: "This field is required" }
    : undefined;
  const grsOrLegacyRcg = useWatch({ control, name: "grs_or_legacy_rcg" });
  const isRcg = grsOrLegacyRcg === "RCG";

  // Single registration point for rcg_carrier, kept even while it's hidden
  // (isRcg false) -- react-hook-form merges newly passed `rules` into a
  // field's existing config rather than replacing it, so a second,
  // conditionally-mounted Controller for the same name (the previous
  // approach here) would leave behind a stale `required` rule once
  // unmounted: switching RCG -> GRS -> RCG -> GRS would permanently mark
  // rcg_carrier required even while hidden, since nothing ever re-registers
  // it without that rule (shouldUnregister isn't set anywhere in this app).
  // Passing an explicit `{ required: false }` (not just omitting `required`)
  // when hidden is deliberate too -- merging `undefined`/`{}` into the
  // existing rules leaves a previously-set `required` untouched, only an
  // explicit `false` actually overwrites it.
  const { field: rcgCarrierField } = useController({
    control,
    name: "rcg_carrier",
    rules: isRcg ? requiredRule : { required: false },
  });
  useEffect(() => {
    if (!isRcg && rcgCarrierField.value) {
      rcgCarrierField.onChange("");
    }
    // rcgCarrierField is a stable object returned by useController --
    // intentionally omitted from the dependency array.
  }, [isRcg]);

  return (
    <>
      {showBeneficiaryClass && (
        <ControlledSelect
          name="beneficiary_class"
          control={control}
          readOnly={readOnly}
          label="Class of Beneficiary"
          options={LIBERTY_BENEFICIARY_CLASS_OPTIONS}
          errors={errors}
          onSave={async (option) => {
            const optionValue =
              option?.value != null ? String(option.value) : "";
            await onFieldSave("beneficiary_class", optionValue);
          }}
        />
      )}

      <ControlledSelect
        name="grs_or_legacy_rcg"
        control={control}
        readOnly={readOnly}
        label="GRS or Legacy RCG File?"
        options={LIBERTY_GRS_OR_LEGACY_RCG_OPTIONS}
        errors={errors}
        rules={requiredRule}
        onSave={async (option) => {
          const optionValue = option?.value != null ? String(option.value) : "";
          await onFieldSave("grs_or_legacy_rcg", optionValue);
        }}
      />

      {isRcg && (
        <Select
          name="rcg_carrier"
          readOnly={readOnly}
          label="RCG Carrier"
          value={LIBERTY_RCG_CARRIER_OPTIONS.find(
            (option) => option.value === rcgCarrierField.value,
          )}
          options={LIBERTY_RCG_CARRIER_OPTIONS}
          isClearable
          backspaceRemovesValue
          onChange={async (option: OptionType) => {
            const optionValue =
              option?.value != null ? String(option.value) : "";
            rcgCarrierField.onChange(optionValue);
            await onFieldSave("rcg_carrier", optionValue);
          }}
          errors={errors}
        />
      )}

      <ControlledSelect
        name="segmentation_type"
        control={control}
        readOnly={readOnly}
        label="Segmentation Type"
        options={LIBERTY_SEGMENTATION_TYPE_OPTIONS}
        errors={errors}
        rules={requiredRule}
        onSave={async (option) => {
          const optionValue = option?.value != null ? String(option.value) : "";
          await onFieldSave("segmentation_type", optionValue);
        }}
      />

      <ControlledSelect
        name="referral_type"
        control={control}
        readOnly={readOnly}
        label="Referral Type"
        options={LIBERTY_REFERRAL_TYPE_OPTIONS}
        errors={errors}
        rules={requiredRule}
        onSave={async (option) => {
          const optionValue = option?.value != null ? String(option.value) : "";
          await onFieldSave("referral_type", optionValue);
        }}
      />

      <ControlledInput
        control={control}
        name="wcrc_case_id"
        label="Case ID Number (MSA)"
        errors={errors}
        readOnly={readOnly}
        maxLength={100}
        componentType={TextInput}
        rules={requiredRule}
        onSave={(value) => onFieldSave("wcrc_case_id", String(value ?? ""))}
      />
    </>
  );
};

export default LibertyMutualReferralFields;
